import type { LacTenantUsersGroup, LacUser } from '../../types/lac';
import { resolveUserRef } from '../../features/user/userDisplay';

export type StatusFilter = 'all' | 'enabled' | 'disabled';

export function matchesStatusFilter(
  user: LacUser,
  statusFilter: StatusFilter,
): boolean {
  if (statusFilter === 'all') {
    return true;
  }
  if (user.enabled == null) {
    return false;
  }
  return statusFilter === 'enabled' ? user.enabled : !user.enabled;
}

export function userListKey(user: LacUser): string {
  return resolveUserRef(user);
}

export function resolveUserKnowledgeBaseCount(
  user: LacUser,
  metricsByUserKey: Map<string, number>,
): number | undefined {
  // Metrics `userId` is the person (Keycloak UUID). Match against User.id /
  // username only — User.userId is the audit creator.
  const candidates = [user.id?.trim(), user.name.trim()].filter(
    (value): value is string => Boolean(value),
  );

  for (const candidate of candidates) {
    const count = metricsByUserKey.get(candidate);
    if (count != null) {
      return count;
    }
  }

  return undefined;
}

export function filterGroup(
  group: LacTenantUsersGroup,
  query: string,
  statusFilter: StatusFilter,
): LacTenantUsersGroup | null {
  const statusFilteredUsers = group.users.filter((user) =>
    matchesStatusFilter(user, statusFilter),
  );

  if (query === '') {
    if (statusFilteredUsers.length === 0 && statusFilter !== 'all') {
      return null;
    }

    return {
      ...group,
      users: statusFilteredUsers,
    };
  }

  const tenantMatches =
    group.displayName.toLowerCase().includes(query) ||
    group.tenantName.toLowerCase().includes(query);

  const users = statusFilteredUsers.filter(
    (user) =>
      user.name.toLowerCase().includes(query) ||
      (user.firstName?.toLowerCase().includes(query) ?? false) ||
      (user.lastName?.toLowerCase().includes(query) ?? false) ||
      (user.email?.toLowerCase().includes(query) ?? false),
  );

  if (!tenantMatches && users.length === 0) {
    return null;
  }

  return {
    ...group,
    users: tenantMatches ? statusFilteredUsers : users,
  };
}
